// Fuentes de datos disponibles para las gráficas personalizadas.
export const DASHBOARD_METRIC_SOURCES = [
  {
    key: "equiposPorCategoria",
    label: "Equipos por categoría",
    metric: "total",
    nameKey: "_id",
  },
  {
    key: "equiposPorMarca",
    label: "Equipos por marca",
    metric: "total",
    nameKey: "_id",
  },
  {
    key: "equiposPorModelo",
    label: "Equipos por modelo",
    metric: "total",
    nameKey: "_id",
  },
  {
    key: "equiposPorArea",
    label: "Equipos por área",
    metric: "total",
    nameKey: "_id",
  },
  {
    key: "equiposPorProveedor",
    label: "Equipos por proveedor",
    metric: "total",
    nameKey: "_id",
  },
];

export const CHART_TYPES = [
  { value: "bar", label: "Barras" },
  { value: "pie", label: "Torta" },
];

// Registro canónico de todos los bloques del dashboard.
// El orden de este array = layout por defecto (reproduce el JSX actual).
export const DASHBOARD_BLOCK_REGISTRY = [
  {
    id: "kpi-clientes",
    family: "kpi",
    label: "KPI · Clientes registrados",
    description: "Tarjeta con el total de clientes.",
  },
  {
    id: "kpi-equipos",
    family: "kpi",
    label: "KPI · Equipos registrados",
    description: "Tarjeta con el total de equipos.",
  },
  {
    id: "kpi-hojas-vida-pendientes",
    family: "kpi",
    label: "KPI · Hojas de vida pendientes",
    description: "Marcas y modelos sin hoja de vida configurada.",
  },
  {
    id: "kpi-rutinas-sin-configurar",
    family: "kpi",
    label: "KPI · Rutinas sin configurar",
    description: "Categorías sin rutina de mantenimiento.",
  },
  {
    id: "kpi-servicios-por-autorizar",
    family: "kpi",
    label: "KPI · Servicios por autorizar",
    description: "Gestiones pendientes de autorización.",
  },
  {
    id: "kpi-alertas-stock",
    family: "kpi",
    label: "KPI · Alertas de stock",
    description: "Artículos por debajo del stock mínimo.",
  },
  {
    id: "estado-mantenimiento",
    family: "section",
    label: "Estado de mantenimiento",
    description:
      "Resumen de equipos vencidos, fuera de periodicidad y no agendados.",
  },
  {
    id: "prioridades-ia",
    family: "section",
    label: "Top 5 prioridades de hoy (IA)",
    description:
      "Prioridades diarias generadas por IA según tus servicios asignados.",
  },
  {
    id: "chart-cronograma",
    family: "chart",
    label: "Calendario de cronograma",
    description: "Fechas clave de actividades programadas.",
  },
  {
    id: "chart-equipos-categoria",
    family: "chart",
    label: "Equipos por categoría",
    description: "Distribución de equipos por categoría.",
  },
  {
    id: "chart-equipos-marca",
    family: "chart",
    label: "Equipos por marca",
    description: "Comparativo de inventario entre marcas.",
  },
  {
    id: "chart-equipos-modelo",
    family: "chart",
    label: "Equipos por modelo",
    description: "Detalle de equipos por modelo.",
  },
  {
    id: "chart-equipos-area",
    family: "chart",
    label: "Equipos por área",
    description: "Participación de áreas con mayor volumen.",
  },
  {
    id: "chart-equipos-proveedor",
    family: "chart",
    label: "Equipos por proveedor",
    description: "Cantidad de equipos por proveedor.",
  },
  {
    id: "tabla-alertas-stock",
    family: "table",
    label: "Tabla · Alertas de stock mínimo",
    description: "Listado de artículos bajo mínimo.",
  },
  {
    id: "tabla-categorias-sin-rutina",
    family: "table",
    label: "Tabla · Categorías sin rutina",
    description: "Categorías sin rutina registrada.",
  },
  {
    id: "tabla-sin-hoja-vida",
    family: "table",
    label: "Tabla · Marcas y modelos sin hoja de vida",
    description: "Combinaciones marca/modelo sin hoja de vida.",
  },
];

const REGISTRY_BY_ID = DASHBOARD_BLOCK_REGISTRY.reduce((acc, b) => {
  acc[b.id] = b;
  return acc;
}, {});
const REGISTRY_IDS = DASHBOARD_BLOCK_REGISTRY.map((b) => b.id);
const METRIC_SOURCE_KEYS = DASHBOARD_METRIC_SOURCES.map((s) => s.key);
const CHART_TYPE_VALUES = CHART_TYPES.map((t) => t.value);

export const DEFAULT_DASHBOARD_LAYOUT = {
  blocks: DASHBOARD_BLOCK_REGISTRY.map((b) => ({ id: b.id, visible: true })),
  customCharts: [],
};

export const sanitizeCustomCharts = (input) => {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of input) {
    if (!raw || typeof raw.id !== "string" || !raw.id) continue;
    if (seen.has(raw.id)) continue;
    if (!METRIC_SOURCE_KEYS.includes(raw.source)) continue;
    seen.add(raw.id);
    const source = DASHBOARD_METRIC_SOURCES.find((s) => s.key === raw.source);
    const maxItemsNum = Number(raw.maxItems);
    out.push({
      id: raw.id,
      title:
        typeof raw.title === "string" && raw.title.trim()
          ? raw.title.trim()
          : "Gráfica personalizada",
      type: CHART_TYPE_VALUES.includes(raw.type) ? raw.type : "bar",
      source: source.key,
      metric: source.metric,
      nameKey: source.nameKey,
      maxItems: Number.isFinite(maxItemsNum)
        ? Math.min(20, Math.max(3, Math.round(maxItemsNum)))
        : 8,
    });
  }
  return out;
};

// stored: { blocks?: [{id, visible}], customCharts?: [...] }
// => { orderedBlocks: [{id, family, custom, visible}], customCharts: [...] }
export const normalizeDashboardLayout = (stored) => {
  const safe = stored && typeof stored === "object" ? stored : {};
  const customCharts = sanitizeCustomCharts(safe.customCharts);
  const validIds = new Set([...REGISTRY_IDS, ...customCharts.map((c) => c.id)]);

  const orderedBlocks = [];
  const placed = new Set();

  if (Array.isArray(safe.blocks)) {
    for (const raw of safe.blocks) {
      const id = raw && typeof raw.id === "string" ? raw.id : null;
      if (!id || !validIds.has(id) || placed.has(id)) continue;
      placed.add(id);
      orderedBlocks.push({
        id,
        family: REGISTRY_BY_ID[id] ? REGISTRY_BY_ID[id].family : "chart",
        custom: !REGISTRY_BY_ID[id],
        visible: raw.visible !== false,
      });
    }
  }

  for (const b of DASHBOARD_BLOCK_REGISTRY) {
    if (placed.has(b.id)) continue;
    placed.add(b.id);
    orderedBlocks.push({ id: b.id, family: b.family, custom: false, visible: true });
  }
  for (const c of customCharts) {
    if (placed.has(c.id)) continue;
    placed.add(c.id);
    orderedBlocks.push({ id: c.id, family: "chart", custom: true, visible: true });
  }

  return { orderedBlocks, customCharts };
};

export const readStoredLayout = () => {
  if (typeof window === "undefined") return {};
  try {
    const user = JSON.parse(window.localStorage.getItem("user")) || {};
    return user.dashboardLayout && typeof user.dashboardLayout === "object"
      ? user.dashboardLayout
      : {};
  } catch {
    return {};
  }
};

// ─── Migración desde la personalización vieja basada en localStorage ───
const legacyKey = (userId) => `dashboard-preferences-v1-${userId || "anon"}`;

const LEGACY_WIDGET_TO_BLOCK = {
  cronograma: "chart-cronograma",
  equiposPorCategoria: "chart-equipos-categoria",
  equiposPorMarca: "chart-equipos-marca",
  equiposPorModelo: "chart-equipos-modelo",
  equiposPorArea: "chart-equipos-area",
  equiposPorProveedor: "chart-equipos-proveedor",
};

export const migrateLegacyPreferences = (userId) => {
  if (typeof window === "undefined") return null;

  let raw;
  try {
    raw = window.localStorage.getItem(legacyKey(userId));
  } catch {
    return null;
  }
  if (!raw) return null;

  const clearKey = () => {
    try {
      window.localStorage.removeItem(legacyKey(userId));
    } catch {
      /* noop */
    }
  };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearKey();
    return null;
  }

  const legacyVisible = Array.isArray(parsed?.visibleWidgets)
    ? parsed.visibleWidgets
    : null;
  const customCharts = sanitizeCustomCharts(parsed?.customCharts);

  const blocks = DASHBOARD_BLOCK_REGISTRY.map((b) => {
    const legacyEntry = Object.entries(LEGACY_WIDGET_TO_BLOCK).find(
      ([, blockId]) => blockId === b.id,
    );
    if (legacyEntry && legacyVisible) {
      return { id: b.id, visible: legacyVisible.includes(legacyEntry[0]) };
    }
    return { id: b.id, visible: true };
  });
  for (const c of customCharts) {
    blocks.push({ id: c.id, visible: true });
  }

  clearKey();
  return { blocks, customCharts };
};
